import { Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AnalyticalPanel, Citation, DataQualityState } from '@atlas/ui';
import { api } from '../../lib/api';
import { PageHeader } from '../../components/shared';
import { PageSkeleton } from '../../components/PageSkeleton';

export function GlossaryEntryPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const { data, isLoading, isError } = useQuery({
    queryKey: ['glossary-entry', slug],
    queryFn: () => api.glossaryEntry(slug),
  });

  if (isLoading) return <PageSkeleton blocks={3} />;
  if (isError || !data) return <DataQualityState state="unavailable" title="Glossary entry not found" />;

  return (
    <div className="space-y-6 reading-measure">
      <Link to="/glossary" className="text-sm text-[var(--color-signal-cyan)]">
        ← Concept atlas
      </Link>
      <PageHeader title={data.term} subtitle={data.category.replace('_', ' ')} />

      <AnalyticalPanel title="One sentence">
        <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">{data.oneSentence}</p>
      </AnalyticalPanel>

      <AnalyticalPanel title="Why it matters">
        <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">{data.whyItMatters}</p>
      </AnalyticalPanel>

      <AnalyticalPanel title="Full explanation">
        <div className="space-y-3 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          {data.fullExplanationMdx.split('\n\n').map((paragraph) => (
            <p key={paragraph}>{paragraph.replaceAll('**', '')}</p>
          ))}
        </div>
      </AnalyticalPanel>

      <AnalyticalPanel title="How to read it">
        <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">{data.howToReadIt}</p>
      </AnalyticalPanel>

      <AnalyticalPanel title="CSC structural lens">
        <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">{data.cscStructuralLens}</p>
      </AnalyticalPanel>

      <div className="grid gap-4 md:grid-cols-2">
        <AnalyticalPanel title="Common misreadings" tone="recessed">
          <ul className="list-disc space-y-1 pl-5 text-sm text-[var(--color-text-secondary)]">
            {data.commonMisreadings.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </AnalyticalPanel>
        <AnalyticalPanel title="Limitations & falsifiers" tone="recessed">
          <ul className="list-disc space-y-1 pl-5 text-sm text-[var(--color-text-secondary)]">
            {data.limitationsAndFalsifiers.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </AnalyticalPanel>
      </div>

      <AnalyticalPanel title="Citations">
        <Citation index={1} title={data.term} meta={`v${data.version} · ${data.reviewedAt.slice(0, 10)}`} />
      </AnalyticalPanel>
    </div>
  );
}
